import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, Check } from 'lucide-react';

const Register = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    farm_name: '',
    farm_type: 'mixed',
    location_lat: '',
    location_lon: ''
  });
  const [loading, setLoading] = useState(false);
  const [gettingLocation, setGettingLocation] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const getLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation not supported by your browser');
      return;
    }
    
    setGettingLocation(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setFormData({
          ...formData,
          location_lat: position.coords.latitude.toFixed(4),
          location_lon: position.coords.longitude.toFixed(4)
        });
        toast.success('Location detected!');
        setGettingLocation(false);
      },
      (error) => {
        toast.error('Could not get location. You can enter coordinates manually.');
        setGettingLocation(false);
      }
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email || !formData.password || !formData.farm_name) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setLoading(true);
    try {
      const data = {
        email: formData.email,
        password: formData.password,
        farm_name: formData.farm_name,
        farm_type: formData.farm_type,
        location_lat: formData.location_lat ? parseFloat(formData.location_lat) : null,
        location_lon: formData.location_lon ? parseFloat(formData.location_lon) : null
      };
      
      await register(data);
      toast.success('Welcome to Fencepost! Your 7-day trial has started.');
      navigate('/app');
    } catch (error) {
      const detail = error.response?.data?.detail;
      let errorMessage = 'Registration failed';
      if (typeof detail === 'string') {
        errorMessage = detail;
      } else if (Array.isArray(detail)) {
        errorMessage = detail.map(e => e.msg || e.message || String(e)).join(', ');
      }
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left Panel - Image (hidden on mobile) */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <img 
          src="https://static.prod-images.emergentagent.com/jobs/69854142-2f21-4e88-b5c7-f137a06c1f84/images/cac4584755dcf44f090a10045c9beee307cdf511d11d4998f251be6fb596766a.png" 
          alt="Farm gate with fence posts at dawn" 
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-l from-background via-background/50 to-transparent" />
      </div>

      {/* Right Panel - Form */}
      <div className="flex-1 flex flex-col justify-center px-4 sm:px-6 lg:px-8 py-12 overflow-y-auto">
        <div className="w-full max-w-md mx-auto">
          <Link to="/" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </Link>
          
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-6">
              <img src="/fencepost-logo.png?v=6" alt="Fencepost" className="w-10 h-10 rounded-lg" />
              <span className="font-heading font-bold text-2xl text-foreground">Fencepost</span>
            </div>
            <h1 className="font-heading text-3xl font-bold text-foreground mb-2">
              Start your free trial
            </h1>
            <p className="text-muted-foreground">
              7 days free, then $9.99/month. Cancel anytime.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                name="email"
                type="email"
                placeholder="you@farm.com"
                value={formData.email}
                onChange={handleChange}
                className="input-farm"
                data-testid="register-email-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password">Password *</Label>
              <Input
                id="password"
                name="password"
                type="password"
                placeholder="At least 6 characters"
                value={formData.password}
                onChange={handleChange}
                className="input-farm"
                data-testid="register-password-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="farm_name">Farm Name *</Label>
              <Input
                id="farm_name"
                name="farm_name"
                type="text"
                placeholder="e.g., Red Mesa Ranch"
                value={formData.farm_name}
                onChange={handleChange}
                className="input-farm"
                data-testid="register-farm-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="farm_type">Farm Type</Label>
              <Select 
                value={formData.farm_type} 
                onValueChange={(value) => setFormData({ ...formData, farm_type: value })}
              >
                <SelectTrigger className="input-farm" data-testid="register-farm-type-select">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="mixed">Mixed (Vegetables & Livestock)</SelectItem>
                  <SelectItem value="vegetables">Vegetables / Crops</SelectItem>
                  <SelectItem value="livestock">Livestock / Ranching</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Location (for weather)</Label>
                <Button 
                  type="button" 
                  variant="ghost" 
                  size="sm"
                  onClick={getLocation}
                  disabled={gettingLocation}
                  className="text-primary hover:text-primary"
                >
                  {gettingLocation ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    'Detect Location'
                  )}
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Input
                  name="location_lat"
                  type="text"
                  placeholder="Latitude"
                  value={formData.location_lat}
                  onChange={handleChange}
                  className="input-farm"
                  data-testid="register-lat-input"
                />
                <Input
                  name="location_lon"
                  type="text"
                  placeholder="Longitude"
                  value={formData.location_lon}
                  onChange={handleChange}
                  className="input-farm"
                  data-testid="register-lon-input"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Optional. Used for weather alerts and local advice.
              </p>
            </div>

            <Button 
              type="submit" 
              className="btn-primary w-full" 
              disabled={loading}
              data-testid="register-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating account...
                </>
              ) : (
                'Start Free Trial'
              )}
            </Button>

            <div className="flex items-start gap-2 text-xs text-muted-foreground">
              <Check className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
              <span>
                By signing up, you agree to our terms of service. 
                Your trial includes full access to all features.
              </span>
            </div>
          </form>

          <p className="mt-8 text-center text-muted-foreground">
            Already have an account?{' '}
            <Link to="/login" className="text-primary hover:underline font-medium">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;
